import { create as createQrCode } from 'qrcode';
import { useEffect, useMemo } from 'react';
import Svg, { G, Path, Rect, Text as SvgText } from 'react-native-svg';

/**
 * On-screen rendering of the printed Gripp Tag (OEM Tag v3) — a 1:1 port of
 * the design tool at `design/tag/index.html` (visual = Figma TAGS 3.0, node
 * 2086:2). Geometry, colors and wordmark paths are copied verbatim from that
 * file so what admins see here matches the print-ready artifact exactly; if
 * the design tool's tag visual changes, update this in lockstep.
 */

/** Figma TAGS 3.0 tag colors, pinned to match `design/tag`'s THEME verbatim.
 * Deliberately NOT Crop Circle token references: the on-screen tag must stay
 * identical to the *printed* artifact, which is fixed by the print pipeline —
 * a future app re-theme must not silently change this rendering. (Today
 * `tokens.colors.brand.core` happens to equal the same #FF5505.) */
const THEME = {
  bg: '#FF5505',
  panel: '#FFFFFF',
  fg: '#FFFFFF',
  qr: '#020202',
} as const;

/** Geometry in design units (Figma node 2086:2, OEM Tag v3). */
const TAG = {
  W: 348,
  H: 426,
  RADIUS: 8,
  PANEL_XY: 12,
  PANEL: 324,
  QR_XY: 24,
  QR: 300,
  ROW_X: 24,
  ROW_Y: 360,
  CODE_SIZE: 22,
  CODE_LS: 0.44,
  CODE_BASELINE: 402,
  CODE_RIGHT: 324,
} as const;

/** GRIPP wordmark: outlined vector paths from Figma node 2086:6
 * (viewBox 0 0 207 42), rendered at the row origin. */
const WORDMARK_PATHS = [
  'M32.9965 27.15H25.4127V17.2447H46.1426V36.6202C40.3159 40.2064 33.5541 42 25.8594 42C17.5367 42 11.1524 40.2064 6.69142 36.6202C2.23048 33.034 0 27.9064 0 21.2234C0 16.9362 0.934003 13.182 2.81584 10.0022C4.69768 6.80751 7.44416 4.34259 11.082 2.60536C14.7209 0.86813 19.0699 0 24.1162 0C30.0272 0 34.8368 1.19045 38.545 3.58619C42.2532 5.98192 44.5956 9.32981 45.5712 13.6595L31.1424 14.8925C30.6541 13.5053 29.8598 12.5245 28.7446 11.9223C27.6293 11.3202 25.9981 11.0255 23.8379 11.0255C20.7151 11.0255 18.4014 11.8383 16.8672 13.4628C15.3479 15.0883 14.5813 17.5532 14.5813 20.8883C14.5813 24.4745 15.4321 27.0521 17.1465 28.6213C18.861 30.1904 21.6907 30.9745 25.6643 30.9745C28.592 30.9745 31.0315 30.6947 32.9688 30.134V27.1638L32.9965 27.15Z',
  'M73.5491 18.7435C75.7796 18.7435 77.3405 18.4489 78.2329 17.8467C79.1253 17.2446 79.571 16.2361 79.571 14.8202C79.571 13.4043 79.1253 12.4521 78.2329 11.8786C77.3405 11.3042 75.8212 11.0105 73.6601 11.0105H66.6339V18.7297H73.5481L73.5491 18.7435ZM80.9101 40.9765L73.4937 28.7744H66.6349V40.9765H53.2659V1.00738H74.386C86.9042 1.00738 93.1638 5.5744 93.1638 14.7223C93.1638 17.4542 92.6339 19.7234 91.5741 21.5595C90.5144 23.3956 88.8138 24.8797 86.4724 26.0424L96.1609 40.9903H80.8962L80.9101 40.9765Z',
  'M100.691 1.00738H115.064V40.9913H100.691V1.00738Z',
  'M142.583 19.2478C144.73 19.2478 146.263 18.9532 147.156 18.351C148.048 17.7489 148.494 16.7403 148.494 15.3244C148.494 13.9086 148.034 12.9564 147.128 12.3542C146.222 11.7521 144.744 11.4574 142.695 11.4574H137.341V19.233H142.583V19.2478ZM123.526 1.00738H143.642C149.957 1.00738 154.668 2.19783 157.763 4.56588C160.858 6.93393 162.419 10.534 162.419 15.3393C162.419 20.1446 160.816 23.8435 157.624 26.1967C154.431 28.5499 149.552 29.7275 142.972 29.7275H137.34V40.9775H123.526V1.00738Z',
  'M187.163 19.2478C189.311 19.2478 190.844 18.9532 191.736 18.351C192.629 17.7489 193.074 16.7403 193.074 15.3244C193.074 13.9086 192.615 12.9564 191.709 12.3542C190.802 11.7521 189.325 11.4574 187.275 11.4574H181.922V19.233H187.163V19.2478ZM168.106 1.00738H188.222C194.537 1.00738 199.249 2.19783 202.344 4.56588C205.439 6.93393 207 10.534 207 15.3393C207 20.1446 205.396 23.8435 202.204 26.1967C199.012 28.5499 194.133 29.7275 187.553 29.7275H181.921V40.9775H168.106V1.00738Z',
] as const;

/** The printed tag's code uses SF Mono Semibold, same woff as the design
 * tool (`design/brand-kit/fonts/`), served from `public/` and lazy-loaded the
 * first time a tag renders so it adds nothing to app boot. The fallbacks only
 * flash while it loads — system monos have no 600 weight, so e.g. Menlo
 * synthesizes a too-bold 700. */
const CODE_FONT_NAME = 'SFMonoSemibold';
const CODE_FONT_FAMILY = `'${CODE_FONT_NAME}', 'SF Mono', ui-monospace, Menlo, 'Roboto Mono', monospace`;

let codeFontLoad: Promise<void> | undefined;
const ensureCodeFont = (): Promise<void> => {
  if (!codeFontLoad) {
    codeFontLoad = new FontFace(CODE_FONT_NAME, 'url(/SFMonoSemibold.woff)', {
      weight: '600',
    })
      .load()
      .then((face) => {
        // The project's TS dom lib predates FontFaceSet.add.
        (document.fonts as unknown as { add(f: FontFace): void }).add(face);
      });
  }
  return codeFontLoad;
};

/**
 * QR modules → ONE union path (run-length merged per row), same as the design
 * tool: separate rects leave anti-aliased hairline seams between modules;
 * interior edges of a single filled path don't. ECC level M matches the
 * backend-issued printed tags.
 */
export const buildQrPath = (value: string, sizeUnits: number): string => {
  const { size, data } = createQrCode(value, {
    errorCorrectionLevel: 'M',
  }).modules;
  const cell = sizeUnits / size;
  let d = '';
  for (let row = 0; row < size; row++) {
    let col = 0;
    while (col < size) {
      if (data[row * size + col]) {
        let run = 1;
        while (col + run < size && data[row * size + col + run]) run++;
        const x = col * cell;
        const y = row * cell;
        const w = run * cell;
        d += `M${x.toFixed(3)} ${y.toFixed(3)}h${w.toFixed(3)}v${cell.toFixed(
          3
        )}h${(-w).toFixed(3)}z`;
        col += run;
      } else {
        col++;
      }
    }
  }
  return d;
};

export type GrippTagSvgProps = {
  /** Full URL the QR encodes (use `createTagUrl` so it is env-correct). */
  tagUrl: string;
  /** Printed code shown bottom-right of the tag. */
  tagCode: string;
  /** On-screen width in px; height follows the locked 348:426 aspect. */
  width?: number;
};

export const GrippTagSvg = ({ tagUrl, tagCode, width = 280 }: GrippTagSvgProps) => {
  const qrPath = useMemo(() => buildQrPath(tagUrl, TAG.QR), [tagUrl]);
  const code = tagCode.trim().toUpperCase();

  // Once the face lands in document.fonts the browser re-renders the <text>
  // with it automatically — no state needed. Web-only view, so FontFace and
  // document are safe; the guard covers jsdom in tests.
  useEffect(() => {
    if (typeof FontFace === 'undefined') return;
    ensureCodeFont().catch((e) => {
      // Fallback mono stack still renders the code, just heavier.
      console.warn('Gripp Tag code font failed to load', e);
    });
  }, []);

  return (
    <Svg
      width={width}
      height={(width * TAG.H) / TAG.W}
      viewBox={`0 0 ${TAG.W} ${TAG.H}`}
    >
      <Rect width={TAG.W} height={TAG.H} rx={TAG.RADIUS} fill={THEME.bg} />
      <Rect
        x={TAG.PANEL_XY}
        y={TAG.PANEL_XY}
        width={TAG.PANEL}
        height={TAG.PANEL}
        fill={THEME.panel}
      />
      <G x={TAG.QR_XY} y={TAG.QR_XY}>
        <Path d={qrPath} fill={THEME.qr} />
      </G>
      <G x={TAG.ROW_X} y={TAG.ROW_Y}>
        {WORDMARK_PATHS.map((d, i) => (
          <Path key={i} d={d} fill={THEME.fg} />
        ))}
      </G>
      <SvgText
        x={TAG.CODE_RIGHT}
        y={TAG.CODE_BASELINE}
        fill={THEME.fg}
        fontSize={TAG.CODE_SIZE}
        fontWeight="600"
        fontFamily={CODE_FONT_FAMILY}
        letterSpacing={TAG.CODE_LS}
        textAnchor="end"
      >
        {code}
      </SvgText>
    </Svg>
  );
};
